<template>
  <div class="disc">
    <!-- Top -->
    <header class="d-top">
      <button class="d-back" @click="$router.push('/Home')">←</button>
      <div class="d-title">
        <span class="d-dot"></span>
        <h1>Discover</h1>
      </div>
      <button class="d-icon" @click="$router.push('/Choose')" aria-label="Grid">
        <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M4 4h7v7H4zM13 4h7v7h-7zM4 13h7v7H4zM13 13h7v7h-7z"/></svg>
      </button>
    </header>

    <main class="d-main">
      <!-- Deck -->
      <div class="d-deck-wrap">
        <!-- ACTION HINTS -->
        <div class="d-hint d-hint--pass"  :class="{on: dragX < -40}">NOPE</div>
        <div class="d-hint d-hint--like"  :class="{on: dragX >  40}">LIKE</div>
        <div class="d-hint d-hint--super" :class="{on: dragY < -60}">SUPER LIKE</div>

        <div class="d-deck">
          <!-- Background cards -->
          <div v-for="(p,i) in visible" :key="p.id"
               class="d-card"
               :class="['d-card--'+i, {'d-card--gone': p._gone==='left' || p._gone==='right' || p._gone==='up'}]"
               :style="cardStyle(i)">
            <div class="d-card__img" :style="{backgroundImage:'url('+p.img+')'}"></div>
            <div class="d-card__shade"></div>

            <div class="d-card__top">
              <span v-if="p.online" class="d-card__online">Online</span>
              <span v-if="p.verified" class="d-card__verified" title="Verified">✓</span>
            </div>

            <div class="d-card__info">
              <div class="d-card__name">
                {{ p.name }}<span class="d-card__age">, {{ p.age }}</span>
                <span v-if="p.verified" class="d-card__tick">✓</span>
              </div>
              <div class="d-card__loc">📍 {{ p.city }} · {{ p.distance }}</div>
              <p class="d-card__bio">{{ p.bio }}</p>
              <div class="d-card__tags">
                <span v-for="t in p.tags" :key="t" class="d-card__tag">{{ t }}</span>
              </div>
            </div>
          </div>

          <!-- Empty state -->
          <div v-if="visible.length===0 && !loading" class="d-empty">
            <div class="d-empty__emoji">🎉</div>
            <h3>You're all caught up!</h3>
            <p>You've seen everyone around you for now.<br/>Come back soon — new faces arrive every day.</p>
            <button class="d-btn" @click="refill">Refresh deck</button>
          </div>

          <!-- Loading -->
          <div v-if="loading" class="d-empty">
            <div class="d-empty__loader"></div>
            <p>Loading matches near you…</p>
          </div>
        </div>
      </div>

      <!-- Actions -->
      <div class="d-actions" v-if="visible.length">
        <button class="d-action d-action--undo" @click="undo" :disabled="history.length===0" aria-label="Undo">
          <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><path d="M3 7v6h6M3 13a9 9 0 1 0 3-7"/></svg>
        </button>
        <button class="d-action d-action--pass" @click="swipe('left')" aria-label="Pass">
          <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>
        </button>
        <button class="d-action d-action--super" @click="swipe('up')" aria-label="Super Like">
          <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor"><path d="M12 2l2.4 7.4H22l-6.2 4.5L18.2 22 12 17.3 5.8 22l2.4-8.1L2 9.4h7.6z"/></svg>
        </button>
        <button class="d-action d-action--like" @click="swipe('right')" aria-label="Like">
          <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor"><path d="M12 21s-7-4.35-9.5-9.14C1.04 9.13 2.4 5.5 5.6 5.5c1.88 0 3.38 1.05 4.4 2.57C11.02 6.55 12.52 5.5 14.4 5.5c3.2 0 4.56 3.63 3.1 6.36C19 16.65 12 21 12 21z"/></svg>
        </button>
        <button class="d-action d-action--boost" @click="boost" aria-label="Boost">
          <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor"><path d="M13 2L3 14h7l-1 8 11-12h-7z"/></svg>
        </button>
      </div>
    </main>

    <!-- Match modal -->
    <transition name="dm">
      <div v-if="matched" class="dm" @click.self="matched=null">
        <div class="dm__box">
          <div class="dm__title">✨ It's a Match! ✨</div>
          <div class="dm__sub">You and {{ matched.name }} liked each other</div>
          <div class="dm__avatars">
            <img :src="matched.img" alt=""/>
            <span>💖</span>
            <img :src="myAvatar" alt=""/>
          </div>
          <div class="dm__actions">
            <button class="d-btn d-btn--ghost" @click="matched=null">Keep swiping</button>
            <button class="d-btn" @click="viewProfile">View profile →</button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
// Reuse profile builder logic locally (minimal duplication — keeps the page self-contained)
const INTERESTS = [
  '☕ Coffee','📚 Books','✈️ Travel','🎬 Movies','🎵 Music','🍳 Cooking',
  '🏋️ Fitness','🎨 Art','🎮 Gaming','🧘 Yoga','🐶 Dogs','🐱 Cats',
  '🌱 Plants','📷 Photography','🍣 Sushi','🍷 Wine','🎿 Skiing','🏖️ Beach'
];
const CITIES = ['New York','Los Angeles','London','Paris','Tokyo','Seoul','Sydney','Madrid','Berlin','Dubai','Toronto','Singapore'];
const IMGS = [
  'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=800&h=1100&fit=crop',
  'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=800&h=1100&fit=crop',
  'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=800&h=1100&fit=crop',
  'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=800&h=1100&fit=crop',
  'https://images.unsplash.com/photo-1529626455594-4ff0802cfb7e?w=800&h=1100&fit=crop',
  'https://images.unsplash.com/photo-1524504388940-b1c1722653e1?w=800&h=1100&fit=crop',
  'https://images.unsplash.com/photo-1517841905240-472988babdf9?w=800&h=1100&fit=crop',
  'https://images.unsplash.com/photo-1488426862026-3ee34a7d66df?w=800&h=1100&fit=crop',
  'https://images.unsplash.com/photo-1534751516642-a1af1ef26a56?w=800&h=1100&fit=crop',
  'https://images.unsplash.com/photo-1517365830460-955ce3ccd263?w=800&h=1100&fit=crop',
  'https://images.unsplash.com/photo-1531123897727-8f129e1688ce?w=800&h=1100&fit=crop',
  'https://images.unsplash.com/photo-1509967419530-da38b4704bc6?w=800&h=1100&fit=crop'
];
const NAMES = ['Sophia','Emma','Aiko','Maya','Noelle','Priya','Lila','Zoe','Chloe','Ava','Ruby','Luna','Mia','Isla','Nina','Yuki','Sora','Elena','Mila','Aria'];
const BIOS = [
  'Coffee dates > dinner dates. Change my mind.',
  'Bookworm with a travel bug. Looking for a partner in crime ✨',
  'I can cook and I\'ll pretend to like your cooking too.',
  'Sunday morning markets, Friday night dancing.',
  'Fluent in three languages: English, sarcasm, and emoji.',
  'Plant mom, dog aunt, wine enthusiast 🍷',
  'Hiking on weekends, Netflix on weekdays.',
  'Tell me a terrible joke. That\'s it, that\'s the bio.'
];

function pseudo(i){ return (Math.sin(i*12.9898)*43758.5453) % 1; }
function pick(arr,i){ return arr[Math.abs(Math.floor(pseudo(i)*arr.length)) % arr.length]; }

function buildProfile(i){
  return {
    id: i,
    name: NAMES[i % NAMES.length],
    age: 22 + (i % 14),
    city: CITIES[i % CITIES.length],
    distance: (1 + (i*7) % 28) + ' km',
    img: IMGS[i % IMGS.length],
    online: (i % 3) !== 0,
    verified: (i % 4) !== 0,
    tags: [ pick(INTERESTS, i*3), pick(INTERESTS, i*7), pick(INTERESTS, i*11) ],
    bio: BIOS[i % BIOS.length],
    _gone: null
  };
}

export default {
  name: 'Discover',
  data(){
    return {
      profiles: [],
      loading: true,
      history: [],
      matched: null,
      myAvatar: 'https://images.unsplash.com/photo-1529626455594-4ff0802cfb7e?w=200&h=200&fit=crop',
      // drag state
      dragging: false, dragStartX: 0, dragStartY: 0, dragX: 0, dragY: 0, dragging_touch: false
    }
  },
  computed:{
    visible(){ return this.profiles.slice(0, 3); }
  },
  created(){
    document.documentElement.classList.add('km-desktop');
    if(!localStorage.getItem('token')){ this.$router.replace('/Login'); return; }
    this.load();
  },
  mounted(){
    // Pointer events for swipe on the top card (stored on instance, not as a data key)
    this.$options._km = {
      down: (e) => this.onDown(e),
      move: (e) => this.onMove(e),
      up:   (e) => this.onUp(e)
    };
    window.addEventListener('pointermove', this.$options._km.move);
    window.addEventListener('pointerup',   this.$options._km.up);
    window.addEventListener('pointercancel', this.$options._km.up);
    const deck = this.$el.querySelector('.d-deck');
    if(deck) deck.addEventListener('pointerdown', this.$options._km.down);
  },
  beforeDestroy(){
    document.documentElement.classList.remove('km-desktop');
    const km = this.$options._km;
    if(km){
      window.removeEventListener('pointermove', km.move);
      window.removeEventListener('pointerup',   km.up);
      window.removeEventListener('pointercancel', km.up);
    }
  },
  methods:{
    async load(){
      this.loading = true;
      try {
        const res = await this.$http({ method:'get', url:'xuanfeilist', data:{ id: 1 } });
        const api = (res && res.data && Array.isArray(res.data)) ? res.data : [];
        const apiNorm = api.filter(r => r.img_url && !/^\/xuanfei/.test(r.img_url) && !/localhost/.test(r.img_url)).map((r,i) => {
          const seed = r.id || (i+1);
          const clean = (r.xuanfei_name || '').replace(/[^\x20-\x7e\u00a0-\uffff]/g,'').trim();
          return {
            id: r.id, name: clean || NAMES[seed % NAMES.length], age: 22 + (seed % 14),
            city: CITIES[seed % CITIES.length], distance: (1 + (seed % 28)) + ' km',
            img: r.img_url, online: (seed % 3) !== 0, verified: (seed % 4) !== 0,
            tags: [ INTERESTS[seed % INTERESTS.length], INTERESTS[(seed*3) % INTERESTS.length] ],
            bio: (r.jianjie || BIOS[seed % BIOS.length]).slice(0,140), _gone: null
          };
        });
        const showcase = Array.from({length: 16}, (_,i) => buildProfile(i+1));
        this.profiles = apiNorm.concat(showcase);
      } catch(e){
        this.profiles = Array.from({length: 16}, (_,i) => buildProfile(i+1));
      }
      this.loading = false;
    },
    refill(){
      const n = this.profiles.length;
      const more = Array.from({length: 10}, (_,i) => buildProfile(n+i+1));
      this.profiles = this.profiles.concat(more);
    },
    cardStyle(i){
      if(i === 0){
        const rot = this.dragX * 0.05;
        return {
          transform: `translate(${this.dragX}px, ${this.dragY}px) rotate(${rot}deg)`,
          transition: this.dragging ? 'none' : 'transform .35s cubic-bezier(.2,.9,.3,1)',
          zIndex: 3
        };
      }
      if(i === 1){
        const scale = 0.95 + Math.min(Math.abs(this.dragX)/600, 0.05);
        return { transform: `scale(${scale}) translateY(10px)`, zIndex: 2 };
      }
      return { transform: 'scale(.9) translateY(20px)', zIndex: 1 };
    },

    // ===== Pointer / gesture =====
    onDown(e){
      if(this.visible.length === 0) return;
      // Ignore clicks that started on an action button
      if(e.target.closest('.d-action, .d-btn, .d-icon, .d-back')) return;
      this.dragging = true;
      this.dragStartX = e.clientX; this.dragStartY = e.clientY;
    },
    onMove(e){
      if(!this.dragging) return;
      this.dragX = e.clientX - this.dragStartX;
      this.dragY = e.clientY - this.dragStartY;
    },
    onUp(){
      if(!this.dragging) return;
      this.dragging = false;
      const THRESH = 100;
      if(this.dragY < -140){ this.swipe('up'); return; }
      if(this.dragX >  THRESH){ this.swipe('right'); return; }
      if(this.dragX < -THRESH){ this.swipe('left'); return; }
      // snap back
      this.dragX = 0; this.dragY = 0;
    },

    // ===== Actions =====
    swipe(dir){
      const top = this.visible[0];
      if(!top) return;
      // animate exit
      if(dir === 'right'){ this.dragX = 700; this.dragY = 20; top._gone = 'right'; }
      if(dir === 'left') { this.dragX = -700; this.dragY = 20; top._gone = 'left'; }
      if(dir === 'up')   { this.dragX = 0;    this.dragY = -800; top._gone = 'up'; }

      this.history.push({ ...top, _dir: dir });
      if(this.history.length > 20) this.history.shift();

      const liked = dir === 'right' || dir === 'up';
      setTimeout(() => {
        this.profiles.shift();
        this.dragX = 0; this.dragY = 0;
        if(liked){
          // Demo: ~35% chance it's a match
          if(Math.random() < 0.35){
            this.matched = top;
          } else {
            if(dir === 'up') this.$toast.success(`Super liked ${top.name} ⭐`);
          }
        }
        if(this.profiles.length < 3) this.refill();
      }, 350);
    },
    undo(){
      if(!this.history.length) return;
      const last = this.history.pop();
      delete last._gone; delete last._dir;
      this.profiles.unshift(last);
      this.$toast('Rewound ↩');
    },
    boost(){ this.$toast('Boost sent! You\'re top profile for 30 min 🚀'); },
    viewProfile(){
      const id = this.matched && this.matched.id;
      this.matched = null;
      if (id) this.$router.push({ path: '/detail', query: { id } });
    }
  }
}
</script>

<style scoped>
.disc {
  min-height: 100vh;
  background:
    radial-gradient(800px 400px at -10% -10%, rgba(255,77,141,.08), transparent 60%),
    radial-gradient(800px 400px at 110% 0%,  rgba(160,32,240,.08), transparent 60%),
    linear-gradient(180deg, #fff9fb 0%, #fff 40%);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif;
  color: #1a1033;
  user-select: none;
  -webkit-user-select: none;
}
.disc * { box-sizing: border-box; }

.d-top {
  position: sticky; top: 0; z-index: 10;
  max-width: 100%; display: flex; align-items: center; gap: 16px;
  padding: 14px 24px;
  background: rgba(255,255,255,.85); backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(26,16,51,.06);
}
.d-back, .d-icon {
  width: 40px; height: 40px; border-radius: 12px; border: 1px solid rgba(26,16,51,.08);
  background: #fff; font-size: 18px; cursor: pointer; color: #1a1033;
  display: flex; align-items: center; justify-content: center;
  transition: border-color .2s, transform .15s;
}
.d-back:hover, .d-icon:hover { border-color: #ff4d8d; }
.d-title { flex: 1; display: flex; align-items: center; gap: 10px; }
.d-dot { width: 10px; height: 10px; border-radius: 50%; background: linear-gradient(135deg,#ff4d8d,#a020f0); box-shadow: 0 0 12px rgba(255,77,141,.6); }
.d-title h1 { margin: 0; font-size: 22px; font-weight: 800; letter-spacing: -.02em; }

.d-main { max-width: 520px; margin: 0 auto; padding: 24px 20px 110px; }

.d-deck-wrap { position: relative; }
.d-hint {
  position: absolute; top: 32px; padding: 10px 22px; border-radius: 12px;
  font-size: 22px; font-weight: 900; letter-spacing: .1em; border: 4px solid;
  opacity: 0; transform: rotate(-14deg); transition: opacity .15s;
  z-index: 5; pointer-events: none;
}
.d-hint--like  { top: 32px; left: 20px;  color: #10b981; border-color: #10b981; transform: rotate(-14deg); }
.d-hint--pass  { top: 32px; right: 20px; color: #ef4444; border-color: #ef4444; transform: rotate(14deg); }
.d-hint--super { top: 60px; left: 50%; transform: translateX(-50%) rotate(-4deg); color: #3b82f6; border-color: #3b82f6; }
.d-hint.on { opacity: 1; }

.d-deck { position: relative; width: 100%; aspect-ratio: 3/4.2; max-height: 640px; margin: 0 auto; }
.d-card {
  position: absolute; inset: 0;
  border-radius: 24px; overflow: hidden;
  box-shadow: 0 25px 60px rgba(26,16,51,.2);
  cursor: grab;
  touch-action: pan-y;
}
.d-card:active { cursor: grabbing; }
.d-card__img { position: absolute; inset: 0; background-size: cover; background-position: center; }
.d-card__shade {
  position: absolute; inset: 0;
  background: linear-gradient(180deg, rgba(0,0,0,0) 40%, rgba(0,0,0,.85) 100%);
}
.d-card__top {
  position: absolute; top: 16px; left: 16px; right: 16px;
  display: flex; justify-content: space-between; align-items: center;
  z-index: 2;
}
.d-card__online {
  background: rgba(16,185,129,.95); color: #fff; font-size: 12px; font-weight: 700;
  padding: 5px 12px; border-radius: 999px; backdrop-filter: blur(6px);
  display: inline-flex; align-items: center; gap: 6px;
}
.d-card__online::before { content: ''; width: 7px; height: 7px; border-radius: 50%; background: #fff; box-shadow: 0 0 0 3px rgba(255,255,255,.4); }
.d-card__verified {
  width: 30px; height: 30px; border-radius: 50%;
  background: linear-gradient(135deg, #3b82f6, #2563eb); color: #fff; font-weight: 800;
  display: inline-flex; align-items: center; justify-content: center;
  box-shadow: 0 4px 12px rgba(59,130,246,.4); border: 2px solid #fff;
}
.d-card__info { position: absolute; bottom: 0; left: 0; right: 0; padding: 20px 22px 22px; color: #fff; z-index: 2; }
.d-card__name { font-size: 28px; font-weight: 800; letter-spacing: -.01em; text-shadow: 0 2px 8px rgba(0,0,0,.4); }
.d-card__age { font-weight: 500; opacity: .9; }
.d-card__tick { font-size: 16px; margin-left: 8px; color: #60a5fa; }
.d-card__loc { font-size: 14px; margin-top: 4px; opacity: .95; }
.d-card__bio { font-size: 14px; margin: 10px 0 12px; opacity: .92; line-height: 1.45; }
.d-card__tags { display: flex; gap: 6px; flex-wrap: wrap; }
.d-card__tag {
  font-size: 11px; font-weight: 600; padding: 4px 10px; border-radius: 999px;
  background: rgba(255,255,255,.22); backdrop-filter: blur(8px); color: #fff;
  border: 1px solid rgba(255,255,255,.22);
}

.d-empty {
  position: absolute; inset: 0; display: flex; flex-direction: column;
  align-items: center; justify-content: center; text-align: center; padding: 40px 20px;
  color: #6b5c85; background: #fff; border-radius: 24px; border: 2px dashed #f0e6ee;
}
.d-empty__emoji { font-size: 54px; margin-bottom: 10px; }
.d-empty h3 { margin: 0 0 6px; font-size: 22px; color: #1a1033; }
.d-empty p { margin: 0 0 20px; }
.d-empty__loader {
  width: 48px; height: 48px; border: 4px solid #f0e6ee; border-top-color: #ff4d8d;
  border-radius: 50%; animation: dspin 1s linear infinite; margin-bottom: 14px;
}
@keyframes dspin { to { transform: rotate(360deg); } }

/* Actions */
.d-actions {
  display: flex; justify-content: center; align-items: center; gap: 16px;
  margin-top: 24px;
}
.d-action {
  width: 60px; height: 60px; border-radius: 50%; border: none;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; background: #fff;
  box-shadow: 0 8px 20px rgba(26,16,51,.1), 0 2px 6px rgba(26,16,51,.06);
  transition: transform .15s, box-shadow .2s;
}
.d-action:hover { transform: translateY(-3px) scale(1.05); }
.d-action:disabled { opacity: .4; cursor: not-allowed; transform: none; }
.d-action--undo  { color: #ffaa00; width: 48px; height: 48px; }
.d-action--pass  { color: #ef4444; }
.d-action--like  { color: #ff4d8d; width: 72px; height: 72px; background: linear-gradient(135deg, #ff4d8d, #a020f0); color: #fff; box-shadow: 0 12px 30px rgba(255,77,141,.4); }
.d-action--like:hover { box-shadow: 0 16px 40px rgba(255,77,141,.55); }
.d-action--super { color: #3b82f6; }
.d-action--boost { color: #a020f0; width: 48px; height: 48px; }

/* Match modal (shared look with Matches page) */
.dm { position: fixed; inset: 0; background: rgba(26,16,51,.75); backdrop-filter: blur(12px); z-index: 200; display: flex; align-items: center; justify-content: center; padding: 20px; }
.dm__box {
  background: linear-gradient(135deg, #1a1033, #4a1a6e); color: #fff;
  border-radius: 28px; padding: 36px; text-align: center; max-width: 400px; width: 100%;
  position: relative; overflow: hidden; box-shadow: 0 40px 80px rgba(0,0,0,.5);
}
.dm__box::before { content: ''; position: absolute; inset: 0; background: radial-gradient(circle at 50% 0%, rgba(255,77,141,.4), transparent 60%); }
.dm__title { font-size: 28px; font-weight: 800; margin-bottom: 8px; background: linear-gradient(90deg, #ffd3e4, #fff); -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent; position: relative; }
.dm__sub { font-size: 15px; opacity: .8; margin-bottom: 24px; position: relative; }
.dm__avatars { display: flex; align-items: center; justify-content: center; gap: 16px; margin-bottom: 28px; position: relative; }
.dm__avatars img { width: 100px; height: 100px; border-radius: 50%; object-fit: cover; border: 4px solid rgba(255,255,255,.2); box-shadow: 0 10px 24px rgba(0,0,0,.3); }
.dm__avatars span { font-size: 36px; animation: beatD 1.2s ease-in-out infinite; }
@keyframes beatD { 0%,100% { transform: scale(1); } 50% { transform: scale(1.2); } }
.dm__actions { display: flex; gap: 10px; position: relative; }

.d-btn { flex: 1; padding: 15px; font-family: inherit; font-weight: 700; font-size: 14px; background: linear-gradient(90deg, #ff4d8d 0%, #a020f0 100%); color: #fff; border: none; border-radius: 14px; cursor: pointer; box-shadow: 0 10px 24px rgba(255,77,141,.3); }
.d-btn--ghost { background: rgba(255,255,255,.15); box-shadow: none; }

.dm-enter-active, .dm-leave-active { transition: opacity .25s ease; }
.dm-enter-active .dm__box { animation: dmPop .5s cubic-bezier(.2,.9,.3,1.2); }
@keyframes dmPop { from { transform: scale(.7); opacity: 0; } to { transform: scale(1); opacity: 1; } }
.dm-enter, .dm-leave-to { opacity: 0; }

/* Responsive */
@media (max-width: 720px) {
  .d-main { padding: 16px 14px 24px; }
  .d-top { padding: 12px 16px; }
  .d-title h1 { font-size: 18px; }
  .d-deck { max-height: none; }
  .d-card__name { font-size: 24px; }
  .d-card__info { padding: 18px; }
  .d-hint { font-size: 18px; padding: 8px 16px; border-width: 3px; }
  .d-action--like { width: 64px; height: 64px; }
  .d-action { width: 54px; height: 54px; }
}
</style>
